<template>
  <v-card>
    <v-card-title>
      Provider Listings
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
    </v-card-title>
    <v-card-text>
      <v-data-table
        v-if="facilities"
        :headers="facilityHeaders"
        :items="facilities"
        show-expand
        :search="search"
      >
        <template v-slot:item.createdAt="{ item }"
          ><span v-if="item.createdAt">{{
            item.createdAt | dateTime
          }}</span></template
        >
        <template v-slot:item.updatedAt="{ item }"
          ><span v-if="item.updatedAt">{{
            item.updatedAt | dateTime
          }}</span></template
        >
        <template v-slot:item.status="{ item }"
          ><span>{{ item.status | facilityStatus }}</span></template
        >
        <template v-slot:item.claimed="{ item }"
          ><span>{{ item.claimed ? "Yes" : "No" }}</span></template
        >

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pt-5 pb-5" v-if="item.connectId">
            <h4>Monthly Upgrade Fee</h4>
            <v-simple-table v-if="item.subscription">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Cancel</th>
                  <th>Renewal Date</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {{ item.subscription.active ? "Active" : "Paused" }}
                  </td>
                  <td>
                    {{ item.subscription.cancel ? "Yes" : "No" }}
                  </td>
                  <td>
                    {{
                      getRenewalDate(
                        item.subscription.startDate,
                        item.subscription.active
                      )
                    }}
                  </td>
                  <td>
                    {{ item.subscription.createdAt | dateTime }}
                  </td>
                  <td>
                    {{ item.subscription.updatedAt | dateTime }}
                  </td>
                  <td>
                    <v-btn
                      v-if="
                        item.subscription.active && !item.subscription.cancel
                      "
                      small
                      color="primary"
                      @click="
                        cancelSubscriptionData = {
                          facilityId: item.externalId,
                          subscription: item.subscription
                        }
                      "
                    >
                      Set to cancel
                    </v-btn>
                    <v-btn
                      v-if="item.active && item.cancel"
                      small
                      color="primary"
                      @click="
                        cancelSubscriptionData = {
                          facilityId: item.externalId,
                          subscription: item.subscription
                        }
                      "
                    >
                      Set to not cancel
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <span v-else>No Monthly Upgrade Fee for this listing</span>

            <h4 class="mt-5">Campaigns</h4>
            <v-simple-table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>Budget</th>
                  <th>Created</th>
                  <th>Updated</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="campaign in item.campaigns" :key="campaign.id">
                  <td>{{ campaign.product | productName }}</td>
                  <td>{{ campaign | campaignStatus }}</td>
                  <td v-if="campaign.startDate">
                    {{ campaign.startDate | dateTime }}
                  </td>
                  <td v-else>None</td>
                  <td>{{ campaign.budget | dollarsCents }}</td>
                  <td>{{ campaign.createdAt | dateTime }}</td>
                  <td>{{ campaign.updatedAt | dateTime }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </td>

          <td :colspan="headers.length" class="pt-5 pb-5" v-else>
            This listing is not in the Provider Platform database.
          </td>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog
      v-if="cancelSubscriptionData"
      v-model="cancelSubscriptionData"
      width="500"
    >
      <v-card>
        <v-card-title class="">
          <span v-if="cancelSubscriptionData.subscription.cancel"
            >Keep monthly upgrade fee active</span
          >
          <span v-else>Cancel subscription</span>
        </v-card-title>

        <v-card-text>
          <p v-if="cancelSubscriptionData.subscription.cancel">
            If you click "Yes", this listing will not be downgraded at the end
            of the billing period.
          </p>
          <p v-else>
            At the end of the current billing period the account will be
            downgraded to Basic.
          </p>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="cancelSubscriptionData = false">
            No
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="toggleCancelSubscription(cancelSubscriptionData)"
            :loading="subCancelLoader"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { getCompanyFacilities } from "../../api/company";
import { setFacilitySubscriptionOverrides } from "../../api/facility";

import {
  startOfToday,
  isSameDay,
  addMonths,
  getDate,
  lastDayOfMonth,
  setDate,
  parseISO,
  format
} from "date-fns";
export default {
  name: "Facilities",
  Title: "Facilities",

  data() {
    return {
      facilities: [],
      search: "",
      facilityHeaders: [
        { text: "Listing ID", value: "externalId" },
        { text: "RPP ID", value: "id" },
        { text: "Name", value: "name" },
        { text: "Status", value: "status" },
        { text: "Account", value: "claimed" },
        { text: "Created", value: "createdAt" },
        { text: "Updated", value: "updatedAt" },
        { text: "Listing Type", value: "product" },
        { text: "", value: "data-table-expand" }
      ],
      subCancelLoader: false,
      cancelSubscriptionData: null
    };
  },

  props: {
    companyId: [String, Number]
  },

  filters: {
    campaignStatus(campaign) {
      if (campaign.status) {
        return "Active";
      } else if (!campaign.status && !campaign.startDate) {
        return "Inactive";
      } else {
        return "Paused";
      }
    },
    productName(product) {
      if (product == "premium-calls") {
        return "Premium calls";
      } else if (product == "premium-clicks") {
        return "Premium clicks";
      }

      return product;
    }
  },

  watch: {
    companyId: function() {
      this.getFacilities();
    }
  },

  mounted() {
    this.getFacilities();
  },

  methods: {
    async getFacilities() {
      const companyId = parseInt(this.companyId);
      if (companyId) {
        this.loading = true;

        const result = await getCompanyFacilities(companyId);
        this.facilities = result.facilities;
        for (const facility of this.facilities) {
          facility.product = "Basic";

          if (
            facility.connectId &&
            facility.subscription &&
            facility.subscription.active
          ) {
            facility.product = "Unlimited Leads";

            const enabledClickCampaign = facility.campaigns.find(
              campaign =>
                campaign.product === "premium-clicks" && campaign.status
            );
            const enabledCallCampaign = facility.campaigns.find(
              campaign =>
                campaign.product === "premium-calls" && campaign.status
            );

            if (enabledClickCampaign && enabledCallCampaign) {
              facility.product = "Premium Clicks & Calls";
            } else if (enabledClickCampaign) {
              facility.product = "Premium Clicks";
            } else if (enabledCallCampaign) {
              facility.product = "Premium Calls";
            }

            if (facility.subscription.cancel) {
              facility.product = "Cancelling";
            }
          }
        }

        this.loading = false;
      }
    },

    async toggleCancelSubscription({ facilityId, subscription }) {
      this.subCancelLoader = true;
      subscription.cancel = !subscription.cancel;
      await setFacilitySubscriptionOverrides(facilityId, !subscription.cancel);
      this.subCancelLoader = false;
      this.cancelSubscriptionData = null;
    },

    getRenewalDate(startDate, active) {
      if (!startDate || !active) {
        return null;
      }
      const today = startOfToday();
      startDate = parseISO(startDate);
      // IF WAS STARTED TODAY, RETURN NEXT MONTH
      if (isSameDay(today, startDate)) {
        return format(addMonths(today, 1), "MM/dd/yyyy");
      }

      // GATHER DATES
      const todayDayOfMonth = getDate(today);
      const lastDayOfThisMonth = lastDayOfMonth(today);
      const lastDayofNextMonth = lastDayOfMonth(addMonths(today, 1));
      const renewDayOfMonth = getDate(startDate);

      let returnDate = null;
      // IF RENEWAL DATE IS GOING TO BE THIS MONTH
      if (todayDayOfMonth <= renewDayOfMonth) {
        if (renewDayOfMonth > lastDayOfThisMonth) {
          returnDate = setDate(today, lastDayOfThisMonth);
        }
        returnDate = setDate(today, renewDayOfMonth);
      }

      // IF RENEWAL DATE IS GOING TO BE NEXT MONTH
      else if (renewDayOfMonth > lastDayofNextMonth) {
        returnDate = lastDayofNextMonth;
      } else {
        returnDate = setDate(lastDayofNextMonth, renewDayOfMonth);
      }

      return format(returnDate, "MM/dd/yyyy");
    }
  }
};
</script>
